import { Observable, Observer, TeardownLogic } from 'rxjs';
import { concat, shareReplay } from 'rxjs/operators';

import { Cacheable } from '../models/cacheable.model';
import { OperationStream, ResultsWithCacheObservableError } from '../models/operation-stream.model';

export class ObservableHelper {
    /**
     * Creates observable which broadcasts results returned by given promises.
     * If networkResults come before cacheResults then cacheResults are dropped.
     * NOTE: Be careful when applying toPromise() to the returned results observable. If both cache and network results fail
     * and there is no updateStream the promise will complete with undefined as per:https://github.com/ReactiveX/rxjs/issues/1736
     *
     * @param cacheResults Promise which returns data from cache.
     * @param networkResults Promise which returns fresh data via network call.
     * @param updateStream Observable which will continue to produce successful results with updated data
     * @param onInitialNetworkError Optional, if specified and there was error in getting fresh data then this handler
     * will be called with reason for failure passed as parameter.
     */
    public static createResultsWithCacheObservable<T extends Cacheable>(
        cacheResults: Promise<T>,
        networkResults: Promise<T>,
        updateStream?: Observable<T>,
        onInitialNetworkError?: (reason: any) => void,
        fallbackResults?: T): OperationStream<T> {
        let results: Observable<T>;
        const initialCompletion = new Promise<ResultsWithCacheObservableError>(resolve => {
            results = Observable.create(
                (observer => {
                    let networkError = null;
                    let networkErrorEncountered = false;
                    let cacheError = null;
                    let cacheErrorEncountered = false;
                    let cacheResultsAvailable = false;

                    const completeWithError = () => {
                        if (fallbackResults && cacheError && networkError) {
                            observer.next(fallbackResults);
                        }
                        resolve(cacheError || networkError ? { cacheError, networkError } : null);
                        observer.complete();
                    };

                    if (cacheResults) {
                        cacheResults.then(data => {
                            cacheResultsAvailable = true;
                            if (data && data instanceof Object && typeof data.fromCache === 'undefined') {
                                data.fromCache = true;
                            }
                            observer.next(data);

                            // In case of network error complete Observable with error
                            if (networkErrorEncountered) {
                                completeWithError();
                            }
                        }).catch(reason => {
                            cacheError = reason;
                            cacheErrorEncountered = true;

                            // In case of network error complete Observable with error
                            if (networkErrorEncountered) {
                                completeWithError();
                            }
                        });
                    }

                    if (networkResults) {
                        networkResults.then(data => {
                            if (data && data instanceof Object && typeof data.fromCache === 'undefined') {
                                data.fromCache = false;
                            }
                            observer.next(data);

                            // No need to wait for cached data
                            completeWithError();
                        }).catch(reason => {
                            networkError = reason;
                            networkErrorEncountered = true;

                            if (onInitialNetworkError) {
                                onInitialNetworkError(reason);
                            }

                            // Wait for cache results before notifying about error
                            if (cacheResultsAvailable || cacheErrorEncountered) {
                                completeWithError();
                            }
                        });
                    }
                }) as (observer: Observer<T>) => TeardownLogic
            );
        });

        if (updateStream) {
            results = results.pipe(concat<T, T>(updateStream));
        }

        // This will ensure that subscriber will get at least one last value that is already available
        results = results.pipe(shareReplay(1));

        return { results, initialCompletion };
    }

    public static createResultsCacheFirstObservable<T extends Cacheable>(
        cacheOnlyRequest: () => Promise<T>,
        networkFirstRequest: () => Promise<T>,
        updateNetworkTimestamp: () => void,
        updateStream?: Observable<T>,
        onInitialNetworkError?: (reason: any) => void): OperationStream<T> {

        let results: Observable<T>;
        const initialCompletion = new Promise<ResultsWithCacheObservableError>(resolve => {
            results = Observable.create(
                (observer => {
                    let networkError = null;
                    let cacheError = null;

                    const completeWithError = () => {
                        resolve(cacheError || networkError ? { cacheError, networkError } : null);
                        observer.complete();
                    };

                    if (cacheOnlyRequest) {
                        cacheOnlyRequest().then(data => {
                            if (data && data instanceof Object && typeof data.fromCache === 'undefined') {
                                data.fromCache = true;
                            }

                            observer.next(data);
                            completeWithError();
                        }).catch(reason => {
                            cacheError = reason;

                            // Make network request if cache failed
                            networkFirstRequest().then(data => {
                                if (data && data instanceof Object && typeof data.fromCache === 'undefined') {
                                    data.fromCache = false;
                                }

                                updateNetworkTimestamp();

                                observer.next(data);
                                completeWithError();
                            }).catch(networkReason => {
                                networkError = networkReason;
                                if (onInitialNetworkError) {
                                    onInitialNetworkError(networkReason);
                                }

                                completeWithError();
                            });
                        });
                    }
                }) as (observer: Observer<T>) => TeardownLogic
            );
        });

        if (updateStream) {
            results = results.pipe(concat<T, T>(updateStream));
        }

        // This will ensure that subscriber will get at least one last value that is already available
        results = results.pipe(shareReplay(1));

        return { results, initialCompletion };
    }
}
